/*
 * File: InheritanceSelector.tsx
 * Project: beaker
 * -----
 * Last Modified: 26 Apr 2022 11:44:52 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Modal, Select, Typography } from 'antd';
import { DataInheritanceLevelType } from 'models/RudyConfigurationModels';
import { LabelInValueType, RawValueType } from 'rc-select/lib/Select';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateCatalogConfigMutation, useGetCatalogConfigQuery } from 'redux/services/rudyCadabby/rudyCadabbyApi';

export const InheritanceSelector: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const divisionId = pathname.includes('/division/') && pathname.split('/')[2];
  const [selectedInheritance, _setSelectedInheritance] = useState<DataInheritanceLevelType | undefined>();
  const [isModalVisible, _setIsModalVisible] = useState(false);

  const [createInhertianceLevel, { isLoading: isCreatingCatalogConfig }] = useCreateCatalogConfigMutation();
  const { data: catalogConfigData, isLoading: isCatalogConfigLoading, isError: isCatalogConfigError } = useGetCatalogConfigQuery({ customerId: divisionId as string }, { skip: !divisionId });

  const handleSelect = (value: RawValueType | LabelInValueType): void => {
    _setSelectedInheritance(value as DataInheritanceLevelType);
    if (!isCatalogConfigError && !isCatalogConfigLoading) {
      _setIsModalVisible(true);
    } else {
      createInhertianceLevel({ customerId: divisionId as string, payload: { dataInheritanceLevel: value as DataInheritanceLevelType } });
    }
  };

  const handleOk = (): void => {
    navigate(`/division/${divisionId}`);
    _setIsModalVisible(false);
    if (divisionId && selectedInheritance) {
      createInhertianceLevel({ customerId: divisionId as string, payload: { dataInheritanceLevel: selectedInheritance as DataInheritanceLevelType } });
      _setSelectedInheritance(selectedInheritance);
    }
  };

  const handleCancel = (): void => {
    _setIsModalVisible(false);
    _setSelectedInheritance(catalogConfigData?.dataInheritanceLevel);
  };

  const warningMessage = (selectedInheritance: DataInheritanceLevelType | undefined): string | undefined => {
    if (catalogConfigData?.dataInheritanceLevel === 'Business' && selectedInheritance === 'Division')
      return "Warning! The product catalog items for this division are currently being inherited from its business. Making this change will delete all product catalog data from this division and you'll have an empty product catalog from which to add items.";
    if (catalogConfigData?.dataInheritanceLevel === 'Business' && selectedInheritance === 'BusinessWithDivisionOverride')
      return 'There will be no initial changes to the product catalog data. Items will continue to be synchronized from the business unless they are explicitly set to override, at which point synchronization will stop for that item and may be changed at the division level.';
    if (catalogConfigData?.dataInheritanceLevel === 'Division' && selectedInheritance === 'Business')
      return 'Warning! All existing data defined for this division’s product catalog will be deleted and replaced with the product catalog from its parent business. Making this change will switch this division’s product catalog items to read-only mode and synchronization with the business will resume.';
    if (catalogConfigData?.dataInheritanceLevel === 'Division' && selectedInheritance === 'BusinessWithDivisionOverride')
      return 'Warning! All existing data defined for this division’s product catalog will be deleted and replaced with the product catalog from its parent business. Making this change will switch this division’s product catalog items to read-only mode and synchronization with the business will resume. Individual items may be overridden as necessary.';
    if (catalogConfigData?.dataInheritanceLevel === 'BusinessWithDivisionOverride' && selectedInheritance === 'Business')
      return 'Warning! All existing data defined for this division’s product catalog will be deleted and replaced with the product catalog from its parent business. Making this change will switch this division’s product catalog items to read-only mode and synchronization with the business will resume. Individual items may be overridden as necessary.';
    if (catalogConfigData?.dataInheritanceLevel === 'BusinessWithDivisionOverride' && selectedInheritance === 'Division')
      return "Warning! Making this change will delete all product catalog data from this division and you'll have an empty product catalog from which to add items.";
  };

  useEffect(() => {
    if (pathname.length === 1) _setSelectedInheritance(undefined);
  }, [pathname]);

  return (
    <>
      <Select
        placeholder="Set an Inheritance Level"
        dropdownMatchSelectWidth={false}
        value={selectedInheritance && !isModalVisible ? selectedInheritance : catalogConfigData?.dataInheritanceLevel}
        onSelect={handleSelect}
        loading={isCatalogConfigLoading || isCreatingCatalogConfig}>
        <Select.Option disabled={catalogConfigData?.dataInheritanceLevel === 'Business'} key="Business">
          Business Only
        </Select.Option>
        <Select.Option disabled={catalogConfigData?.dataInheritanceLevel === 'Division'} key="Division">
          Division Only
        </Select.Option>
        <Select.Option disabled={catalogConfigData?.dataInheritanceLevel === 'BusinessWithDivisionOverride'} key="BusinessWithDivisionOverride">
          Business with Division Override
        </Select.Option>
      </Select>
      <Modal title="Are you sure?" visible={isModalVisible} onOk={handleOk} okText={'Change Inhertiance Level'} onCancel={handleCancel}>
        <Typography.Text type="danger">{warningMessage(selectedInheritance)}</Typography.Text>
      </Modal>
    </>
  );
};
